import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIMEOUT = 14 * 60 * 1000; // 14 minutes
const COUNTDOWN_DURATION = 60 * 1000; // 1 minute countdown

export const useSessionTimeout = () => {
  const { logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isExpired, setIsExpired] = useState(false);
  
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    // Reset states
    setShowWarning(false);
    setCountdown(60);
    setIsExpired(false);
    
    // Set warning timeout
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(60);
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setIsExpired(true);
            // Auto logout when countdown reaches 0
            setTimeout(() => {
              logout();
            }, 100);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, WARNING_TIMEOUT);
    
    // Set final session timeout
    sessionTimeoutRef.current = setTimeout(() => {
      logout();
    }, SESSION_TIMEOUT);
  }, [logout]);

  const extendSession = useCallback(() => {
    // Allow session extension even if warning is not showing (for manual extension)
    if (!isExpired) {
      console.log('Extending session...');
      resetSession();
    }
  }, [isExpired, resetSession]);

  const handleLogout = useCallback(() => {
    logout();
    // Clear all timers
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  }, [logout]);

  // Track user activity
  const trackActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Always update last activity time
    lastActivityRef.current = now;
    
    // If warning is showing and user interacts, hide warning and reset session
    if (showWarning && !isExpired) {
      console.log('User activity detected, extending session...');
      resetSession();
    }
  }, [showWarning, isExpired, resetSession]);

  useEffect(() => {
    resetSession();
    
    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => {
      trackActivity();
    };
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    return () => {
      // Cleanup
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [resetSession, trackActivity]);

  return {
    showWarning,
    countdown,
    isExpired,
    extendSession,
    handleLogout
  };
};
